import { useNavigate } from 'react-router-dom';
import { SearchResultCard, type SearchResult } from '@shared/components/search/SearchResultCard';
import { QuickSearch } from '@shared/components/search/QuickSearch';
import { useRecentCasesByDay, type JurisprudentieWithRelations } from '@shared/hooks/useJurisprudentie';
import { useUserProfile } from '@shared/hooks/useUserProfile';
import { usePortalIds } from '@shared/config';
import { AlertCircle, ChevronDown, CalendarDays } from 'lucide-react';
import { Alert, AlertDescription } from '@shared/components/ui/alert';
import { Button } from '@shared/components/ui/button';
import { Card, CardContent, CardHeader } from '@shared/components/ui/card';
import { Skeleton } from '@shared/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@shared/components/ui/collapsible';
import { useState } from 'react';
import type { DayGroup } from '@shared/lib/dateUtils';

export default function RecentCasesPage() {
  const navigate = useNavigate();
  const { data: profileData } = useUserProfile();
  const portalIds = usePortalIds();
  const { data, isLoading, error } = useRecentCasesByDay(portalIds);
  const [expandedDays, setExpandedDays] = useState<Set<string>>(
    new Set(['0']) // First day (today) expanded by default
  );

  const handleCaseClick = (caseItem: JurisprudentieWithRelations) => {
    navigate(`/case/${caseItem.id}`);
  };

  const toggleDay = (dayIndex: string) => {
    setExpandedDays(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dayIndex)) {
        newSet.delete(dayIndex);
      } else {
        newSet.add(dayIndex);
      }
      return newSet;
    });
  };

  const mapToSearchResult = (caseItem: JurisprudentieWithRelations): SearchResult => {
    const rechtsgebied = caseItem.jurisprudentie_rechtsgebieden?.[0]?.rechtsgebieden?.naam || 'Algemeen';

    return {
      id: caseItem.id,
      ecli: caseItem.ecli,
      title: caseItem.titel,
      date: new Date(caseItem.uitspraak_datum).toLocaleDateString('nl-NL', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }),
      court: caseItem.instanties?.naam || 'Onbekende instantie',
      domain: rechtsgebied,
      summary: caseItem.samenvatting || 'Geen samenvatting beschikbaar',
      relevance: 0,
      rechtsgebieden: caseItem.jurisprudentie_rechtsgebieden?.map(jr => ({
        naam: jr.rechtsgebieden?.naam || ''
      })),
      zaak_types: caseItem.jurisprudentie_zaak_types?.map(jz => ({
        naam: jz.zaak_types?.naam || ''
      })),
      themas: caseItem.jurisprudentie_themas?.map(jt => ({
        naam: jt.themas?.naam || ''
      })),
      portal: caseItem.portal
    };
  };

  const renderDayGroup = (dayGroup: DayGroup<JurisprudentieWithRelations>, index: number) => {
    const dayKey = index.toString();
    const isExpanded = expandedDays.has(dayKey);
    const hasNoContent = dayGroup.count === 0;

    return (
      <div key={dayKey} className="mb-6">
        <Collapsible open={isExpanded} onOpenChange={() => toggleDay(dayKey)}>
          <CollapsibleTrigger asChild>
            <button
              className="flex w-full items-center justify-between p-4 bg-card rounded-xl border border-border/50 shadow-sm hover:shadow-md hover:border-primary/30 transition-all duration-200 group sticky top-16 z-10 backdrop-blur-sm dark:border-border"
              disabled={hasNoContent}
              aria-expanded={isExpanded}
              aria-label={`${dayGroup.dateLabel}, ${dayGroup.count} ${dayGroup.count === 1 ? 'uitspraak' : 'uitspraken'}. ${hasNoContent ? 'Geen inhoud beschikbaar' : isExpanded ? 'Klik om te sluiten' : 'Klik om te openen'}`}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                  <CalendarDays className="h-5 w-5 text-primary" aria-hidden="true" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-lg text-foreground">
                    {dayGroup.dateLabel}
                  </h3>
                  {dayGroup.dateLabel !== dayGroup.formattedDate && (
                    <p className="text-sm text-muted-foreground">
                      {dayGroup.formattedDate}
                    </p>
                  )}
                </div>
                <span className="ml-2 px-2.5 py-1 bg-primary/10 text-primary border border-primary/20 rounded-lg text-sm font-medium" aria-hidden="true">
                  {dayGroup.count} {dayGroup.count === 1 ? 'uitspraak' : 'uitspraken'}
                </span>
              </div>
              {!hasNoContent && (
                <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} aria-hidden="true">
                  <ChevronDown className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              )}
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent>
            {hasNoContent ? (
              <div className="py-8 text-center text-muted-foreground">
                <p>(Nog) geen uitspraken op deze dag</p>
              </div>
            ) : (
              <div className="grid gap-4 mt-4">
                {dayGroup.items.map((caseItem) => (
                  <SearchResultCard
                    key={caseItem.id}
                    result={mapToSearchResult(caseItem)}
                    onClick={() => handleCaseClick(caseItem)}
                  />
                ))}
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </div>
    );
  };

  const renderLoadingSkeleton = () => (
    <>
      {/* Welcome Card Skeleton */}
      <Card className="mb-8 border-primary/20">
        <CardHeader>
          <Skeleton className="h-8 w-64 mb-2" />
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5" />
              <Skeleton className="h-5 w-48" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Page Title Skeleton */}
      <div className="mb-6">
        <Skeleton className="h-7 w-80 mb-2" />
      </div>

      {/* Day Groups Skeleton */}
      {[0, 1, 2, 3, 4].map((index) => (
        <div key={index} className="mb-6">
          <Skeleton className={`h-16 w-full rounded-xl mb-4 ${index > 0 ? `[animation-delay:${index * 75}ms]` : ''}`} />
          {index === 0 && (
            <div className="grid gap-4">
              <Skeleton className="h-32 w-full rounded-xl" />
              <Skeleton className="h-32 w-full rounded-xl [animation-delay:75ms]" />
              <Skeleton className="h-32 w-full rounded-xl [animation-delay:150ms]" />
            </div>
          )}
        </div>
      ))}
    </>
  );

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Decorative blur circles */}
      <div className="absolute top-0 right-0 -mr-20 w-72 h-72 bg-primary/10 dark:bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 left-0 -ml-32 w-64 h-64 bg-blue-500/10 dark:bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative container mx-auto px-4 py-8 max-w-6xl">
        {/* Welcome Section */}
        <Card className="mb-8 rounded-2xl border-border/50 shadow-sm relative overflow-hidden dark:border-border">
          {/* Hero pattern overlay */}
          <div className="absolute inset-0 hero-pattern opacity-40 pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-primary/10 pointer-events-none" />

          <CardHeader className="relative">
            <div className="flex flex-col gap-2">
              <h1 className="text-2xl font-bold text-foreground">
                Welkom terug{profileData?.volledige_naam ? `, ${profileData.volledige_naam}` : ''}!
              </h1>
              <p className="text-muted-foreground">
                Blijf op de hoogte van de nieuwste uitspraken
              </p>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 relative">
            {/* Quick Search Component */}
            <QuickSearch />

            {/* Statistics */}
            {data && (
              <div className="flex flex-wrap gap-4 pt-2 border-t">
                <div className="flex items-center gap-2">
                  <CalendarDays className="h-5 w-5 text-primary" />
                  <span className="text-sm font-medium">
                    Afgelopen 5 werkdagen: <span className="text-primary">{data.weeklyTotal} nieuwe uitspraken gepubliceerd</span>
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Page Title */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2 text-foreground">Publicaties van de laatste 5 werkdagen</h2>
        </div>

        {/* Day Groups */}
        {isLoading ? (
          renderLoadingSkeleton()
        ) : error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error instanceof Error ? error.message : 'Er is een fout opgetreden bij het laden van recente uitspraken'}
            </AlertDescription>
          </Alert>
        ) : !data || data.dayGroups.length === 0 ? (
          <Card className="py-16 text-center">
            <CardContent>
              <p className="text-muted-foreground mb-4">
                {data?.totalCount === 0
                  ? 'De database wordt momenteel gevuld. Kom binnenkort terug.'
                  : 'Geen nieuwe publicaties in de afgelopen 5 werkdagen.'}
              </p>
              {data?.totalCount !== 0 && (
                <Button onClick={() => navigate('/search')} variant="outline">
                  Bekijk oudere uitspraken
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div>
            {data.dayGroups.map((dayGroup, index) => renderDayGroup(dayGroup, index))}
          </div>
        )}
      </div>
    </div>
  );
}